import React, { useState, useEffect } from "react";
import { Box, MenuItem, Select, Typography, styled } from "@mui/material";
import { useProductQuery, useModuleQuery, useTestCaseQuery } from "../../useQuery";
import AutocompleteComponent from "./AutoCompleteComponent";
import { useSelector, useDispatch } from "react-redux";
import {
  updatePreProcessingProduct,
  updatePreProcessingModule,
  updatePostProcessingProduct,
  updatePostProcessingModule,
} from "../../Redux/ProcessingSettings/ProcessingSettings";

const StyledSelect = styled(Select)`
  border-radius: 5px;
  border: 1px solid #d1dbff;
  font-size: 16px;
  color: #2b3539;
  min-width: 180px;
  max-height: 300px;
  background-color: #ffffff;
  .MuiSelect-select {
    padding: 13px 15px;
  }
`;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: "300px",
    },
  },
};

const StyledLabel = styled(Typography)`
  color: #8089a8;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 4px;
`;

const TestCaseSearch = ({ onTestCaseSelect, initialValue = null, tabId = "pre" }) => {
  const dispatch = useDispatch();
  const [searchKeyword, setSearchKeyword] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");

  const processingConfig = useSelector((state) =>
    tabId === "pre"
      ? state.processingSettings.preProcessing
      : state.processingSettings.postProcessing,
  );

  const { product, module } = processingConfig;

  useEffect(() => {
    if (initialValue) {
      if (initialValue.idproducts) {
        dispatch(
          tabId === "pre"
            ? updatePreProcessingProduct(initialValue.idproducts.toString())
            : updatePostProcessingProduct(initialValue.idproducts.toString()),
        );
      }
      if (initialValue.idmodules) {
        dispatch(
          tabId === "pre"
            ? updatePreProcessingModule(initialValue.idmodules.toString())
            : updatePostProcessingModule(initialValue.idmodules.toString()),
        );
      }
}
  }, [initialValue, tabId, dispatch]);

  const { data: products } = useProductQuery();
const { data: modules } = useModuleQuery(product, {
  enabled: !!product && product !== "" && product !== 0,
});
  const {
    data: testCases,
    isLoading: isLoadingTestcase,
    refetch: refetchTestCases,
  } = useTestCaseQuery(module, debouncedSearchValue, {
    enabled: module !== "" && module !== 0,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchKeyword);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchKeyword]);

  useEffect(() => {
    if (module && debouncedSearchValue !== undefined) {
      refetchTestCases();
    }
  }, [debouncedSearchValue, module, refetchTestCases]);

  const handleProductChange = (event) => {
  const newProduct = event.target.value;
  dispatch(
      tabId === "pre"
        ? updatePreProcessingProduct(newProduct)
        : updatePostProcessingProduct(newProduct),
    );
    setSearchKeyword("");
    onTestCaseSelect(null);
  };

  const handleModuleChange = (event) => {
    const newModule = event.target.value;
    dispatch(
      tabId === "pre"
        ? updatePreProcessingModule(newModule)
        : updatePostProcessingModule(newModule),
    );
    setSearchKeyword("");
    onTestCaseSelect(null);
  };

  const handleTestCaseChange = (event, newValue) => {
    if (onTestCaseSelect) {
      onTestCaseSelect(newValue || null);
    }
  };

  const handleInputChange = (event, value) => {
    setSearchKeyword(value || "");
  };

  const testCaseOptions = Array.isArray(testCases) ? testCases : [];

  return (
    <Box display="flex" gap={2} alignItems="flex-start">
      <Box flex={1}>
        <StyledLabel>Application</StyledLabel>
        <StyledSelect
          MenuProps={MenuProps}
          value={product  || ""}
          onChange={handleProductChange}
          displayEmpty
          fullWidth
        >
          <MenuItem disabled value="">
            Application
          </MenuItem>
          {products?.map((product) => (
            <MenuItem key={product.idproducts} value={product.idproducts}>
              {product.productname}
            </MenuItem>
          ))}
        </StyledSelect>
      </Box>

      <Box flex={1}>
        <StyledLabel>Module</StyledLabel>
        <StyledSelect
          MenuProps={MenuProps}
          value={module || ""}
          displayEmpty
          onChange={handleModuleChange}
          disabled={!product}
          fullWidth
        >
          <MenuItem disabled value="">
            Module
          </MenuItem>
          {modules?.map((module) => (
            <MenuItem key={module.idmodules} value={module.idmodules}>
              {module.modulename}
            </MenuItem>
          ))}
        </StyledSelect>
      </Box>

      <Box flex={2}>
        <StyledLabel>Test Scenario</StyledLabel>
        <AutocompleteComponent
          disabled={!module}
          options={testCaseOptions}
          value={initialValue}
          onChange={handleTestCaseChange}
          isLoading={isLoadingTestcase}
          onInputChange={handleInputChange}
          getOptionLabel={(testCase) => testCase?.Test_Case || ""}
          placeholder="Search Test Cases"
          inputValue={searchKeyword}
        />
      </Box>
    </Box>
  );
};

export default TestCaseSearch;
